/**
 * 文件下载 Hook
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/** 下载后延迟释放 Object URL 的等待时间 (ms) */
const URL_REVOKE_DELAY = 10_000;

/**
 * useDownload 返回值
 */
export interface UseDownloadReturn {
  /** 下载中状态 */
  downloading: boolean;
  /** 执行下载 (从 URL) */
  downloadFromUrl: (url: string, filename?: string) => Promise<void>;
  /** 执行下载 (从 Blob) */
  downloadFromBlob: (blob: Blob, filename: string) => void;
  /** 执行下载 (从 API 响应) */
  downloadFromApi: (
    apiFn: () => Promise<Response | Blob>,
    filename?: string,
  ) => Promise<void>;
  /** 下载 JSON 数据为文件 */
  downloadJSON: (
    data: unknown,
    filename: string,
    options?: DownloadJSONOptions,
  ) => void;
}

/**
 * downloadJSON 选项
 */
export interface DownloadJSONOptions {
  /** 是否在文件名中添加时间戳，默认 true */
  addTimestamp?: boolean;
  /** 日期格式函数（默认使用 ISO 日期格式 YYYYMMDDHHmmss） */
  formatDate?: (date: Date) => string;
}

/**
 * 从 Content-Disposition 头中解析文件名
 */
function parseFilenameFromHeader(
  contentDisposition: string | null,
): string | null {
  if (!contentDisposition) return null;

  // 尝试 filename*=UTF-8''xxx 格式
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  // 尝试 filename="xxx" 格式
  const quotedMatch = contentDisposition.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }

  // 尝试 filename=xxx 格式
  const plainMatch = contentDisposition.match(/filename=([^;\s]+)/i);
  if (plainMatch?.[1]) {
    return plainMatch[1];
  }

  return null;
}

/**
 * 触发浏览器下载（延迟释放 Object URL，确保下载已启动）
 *
 * @returns 清理函数，可在组件卸载时调用以释放资源
 */
function triggerDownload(
  blob: Blob,
  filename: string,
): ReturnType<typeof setTimeout> {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // 延迟释放 — 浏览器需要时间启动下载，立即释放可能导致下载失败
  return setTimeout(() => URL.revokeObjectURL(url), URL_REVOKE_DELAY);
}

/**
 * 文件下载 Hook
 *
 * 支持从 URL、Blob、API 响应等多种方式下载文件。
 *
 * @example
 * ```tsx
 * const { downloading, downloadFromUrl, downloadFromApi } = useDownload();
 *
 * // 从 URL 下载
 * <Button onClick={() => downloadFromUrl('/api/export/users', 'users.xlsx')}>
 *   导出
 * </Button>
 *
 * // 从 API 下载
 * <Button
 *   loading={downloading}
 *   onClick={() => downloadFromApi(() => exportUsers(), 'users.xlsx')}
 * >
 *   导出
 * </Button>
 * ```
 */
export function useDownload(): UseDownloadReturn {
  const [downloading, setDownloading] = useState(false);
  const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  // 组件卸载时清理所有 pending timers
  useEffect(() => {
    return () => {
      for (const timer of timersRef.current) {
        clearTimeout(timer);
      }
      timersRef.current.clear();
    };
  }, []);

  /** 跟踪下载定时器，完成后自动从集合中移除 */
  const trackDownload = useCallback((blob: Blob, filename: string) => {
    const timer = triggerDownload(blob, filename);
    timersRef.current.add(timer);
    // timer 到期后自动移除
    setTimeout(() => timersRef.current.delete(timer), URL_REVOKE_DELAY + 500);
  }, []);

  const downloadFromBlob = useCallback(
    (blob: Blob, filename: string) => {
      trackDownload(blob, filename);
    },
    [trackDownload],
  );

  const downloadFromUrl = useCallback(
    async (url: string, filename?: string) => {
      setDownloading(true);
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Download failed: HTTP ${response.status}`);
        }

        const blob = await response.blob();
        const name =
          filename ||
          parseFilenameFromHeader(
            response.headers.get('content-disposition'),
          ) ||
          'download';

        trackDownload(blob, name);
      } finally {
        setDownloading(false);
      }
    },
    [trackDownload],
  );

  const downloadFromApi = useCallback(
    async (apiFn: () => Promise<Response | Blob>, filename?: string) => {
      setDownloading(true);
      try {
        const result = await apiFn();

        if (result instanceof Blob) {
          trackDownload(result, filename || 'download');
          return;
        }

        // Response 对象
        if (result instanceof Response) {
          if (!result.ok) {
            throw new Error(`Download failed: HTTP ${result.status}`);
          }
          const blob = await result.blob();
          const name =
            filename ||
            parseFilenameFromHeader(
              result.headers.get('content-disposition'),
            ) ||
            'download';
          trackDownload(blob, name);
        }
      } finally {
        setDownloading(false);
      }
    },
    [trackDownload],
  );

  const downloadJSON = useCallback(
    (data: unknown, filename: string, options?: DownloadJSONOptions) => {
      const { addTimestamp = true, formatDate: formatDateFn } = options ?? {};

      let finalFilename = filename;
      if (addTimestamp) {
        const now = new Date();
        const timestamp = formatDateFn
          ? formatDateFn(now)
          : `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
        const parts = finalFilename.split('.');
        if (parts.length > 1) {
          const ext = parts.pop();
          finalFilename = `${parts.join('.')}_${timestamp}.${ext}`;
        } else {
          finalFilename = `${finalFilename}_${timestamp}.json`;
        }
      }

      // JSON.stringify 可能抛出异常（循环引用、BigInt 等），需捕获
      const jsonStr = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      trackDownload(blob, finalFilename);
    },
    [trackDownload],
  );

  return {
    downloading,
    downloadFromUrl,
    downloadFromBlob,
    downloadFromApi,
    downloadJSON,
  };
}
